import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { colors } from './styles/theme';

// 페이지는 라우트에 들어갈 때 내려받는다. 전에는 21개가 main.js 하나(612KB)라
// 로그인 안 한 방문자도 관리자 페이지까지 받았다.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AnnouncementList = lazy(() => import('./pages/AnnouncementList'));
const AnnouncementDetail = lazy(() => import('./pages/AnnouncementDetail'));
const Login = lazy(() => import('./pages/Login'));
const SignUp = lazy(() => import('./pages/SignUp'));
const Condition = lazy(() => import('./pages/Condition'));
const Recommend = lazy(() => import('./pages/Recommend'));
const Chat = lazy(() => import('./pages/Chat'));
const Mypage = lazy(() => import('./pages/Mypage'));
const Admin = lazy(() => import('./pages/Admin'));
const Compare = lazy(() => import('./pages/Compare'));
const Guide = lazy(() => import('./pages/Guide'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Pending = lazy(() => import('./pages/Pending'));
const Report = lazy(() => import('./pages/Report'));
const ScoreCalculator = lazy(() => import('./pages/ScoreCalculator'));
const Strategy = lazy(() => import('./pages/Strategy'));
const Today = lazy(() => import('./pages/Today'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Apps = lazy(() => import('./pages/Apps'));


function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  const location = useLocation();

  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        return <Navigate to="/login" state={{ from: location.pathname }} />;
      }
    } catch (e) {
      // 손상된 토큰은 통과시키지 않고 로그아웃 처리
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return <Navigate to="/login" state={{ from: location.pathname }} />;
    }
  }

  return token ? children : <Navigate to="/login" state={{ from: location.pathname }} />;
}

function AppContent() {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ background: colors.grey50, minHeight: '100vh' }}>
      <Header />
      <div style={{ paddingBottom: user && isMobile ? '64px' : 0 }}>
        {/* 스킵 링크(#main)의 착지점. 전에는 페이지 두 곳만 id="main" 을 가져 나머지 20곳에선 링크가 헛돌았다. */}
        <main id="main">
        <Suspense fallback={<div style={{ padding: '48px 16px', textAlign: 'center', color: colors.grey600 }}>불러오는 중…</div>}>
        <Routes>
          {/* 비로그인 접근 가능 */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/pending" element={<Pending />} />
          <Route path="/report" element={<Report />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* 공고 열람은 비로그인 공개 (SEO 색인 대상) */}
          <Route path="/announcements" element={<AnnouncementList />} />
          <Route path="/announcements/:id" element={<AnnouncementDetail />} />

          {/* 홈: 로그인했으면 대시보드, 아니면 공고 목록.
              전에는 비로그인 방문자가 곧장 /login으로 튕겨, 로그인 없이도 공고를
              전부 볼 수 있다는 이 서비스의 강점이 첫 화면에서 가려졌다. */}
          {/* 로그인한 홈은 앱 선택이다 — 메뉴의 "앱 전환"을 모르는 사람도 로고 하나로 돌아온다.
              숲푸딩 자체 대시보드는 /dashboard 로 갔다. (2026-09-19) */}
          <Route path="/" element={
            localStorage.getItem('token')
              ? <PrivateRoute><Apps /></PrivateRoute>
              : <Navigate to="/announcements" replace />
          } />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />

          {/* 로그인 필요 */}
          {/* 통합 로그인 뒤 첫 화면 — 어느 앱으로 갈지 고른다 */}
          <Route path="/apps" element={<PrivateRoute><Apps /></PrivateRoute>} />
          <Route path="/recommend" element={<PrivateRoute><Recommend /></PrivateRoute>} />
          <Route path="/chat" element={<PrivateRoute><Chat /></PrivateRoute>} />
          <Route path="/mypage/condition" element={<PrivateRoute><Condition /></PrivateRoute>} />
          <Route path="/mypage" element={<PrivateRoute><Mypage /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
          <Route path="/compare" element={<PrivateRoute><Compare /></PrivateRoute>} />
          <Route path="/calendar" element={<PrivateRoute><Calendar /></PrivateRoute>} />
          <Route path="/score" element={<PrivateRoute><ScoreCalculator /></PrivateRoute>} />
          <Route path="/strategy" element={<PrivateRoute><Strategy /></PrivateRoute>} />
          <Route path="/today" element={<Today />} />

          {/* 매칭 안 되는 경로: 404 (noindex) */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </main>
        <Footer />
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;